import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";

import AccountTree from "@mui/icons-material/AccountTree";
import Code from "@mui/icons-material/Code";
import Folder from "@mui/icons-material/Folder";
import SearchIcon from "@mui/icons-material/Search";
import Settings from "@mui/icons-material/Settings";
import Square from "@mui/icons-material/Square";
import {
	Box,
	Button,
	Checkbox,
	Chip,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid2,
	List,
	ListItem,
	ListItemIcon,
	ListItemText,
	Paper,
	SvgIcon,
	TextField,
	Typography,
	alpha,
	styled,
} from "@mui/material";
import { SimpleTreeView, TreeItem, TreeItemProps } from "@mui/x-tree-view";
import { animated, useSpring } from "@react-spring/web";
import { LazyLoading } from "../LazyLoading/LazyLoading";

export interface TreeNode {
	id: number;
	name: string;
	isCode: boolean;
	isActive: boolean;
	isInternal?: boolean;
	isSelectable?: boolean;
	isLeaf?: boolean;
	children?: TreeNode[];
	delayCategoryColor?: string;
	sortOrder?: number | null;
	parentName?: string;
}

interface ModalTreeFilterControlProps {
	onClose: (value?: number, name?: string, node?: TreeNode) => void;
	value?: number;
	open: boolean;
	title: string;
	data: TreeNode;
	selectBranch: boolean;
	selectActive: boolean;
	selectInternal: boolean;
	multipleSelectNodes: boolean;
	onHide?: (value: boolean) => void;
	listAssetDrawings?: number[];
	setListAssetDrawings?: React.Dispatch<React.SetStateAction<number[]>>;
	onSuccess?: (value: boolean) => void;
	isLoading: boolean;
	mostUsedCount: number;
	sWithParent: String[];
	dialogMaxWidth: "xs" | "sm" | "md" | "lg" | "xl";
	allowSearch?: boolean;
}

interface SearchResult {
	id: number;
	name: string;
	path: string;
	isCode: boolean;
	hasChildren: boolean;
	node: TreeNode;
}

function MinusSquare(props: any) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 11.023h-11.826q-.375 0-.669.281t-.294.682v0q0 .401.294 .682t.669.281h11.826q.375 0 .669-.281t.294-.682v0q0-.401-.294-.682t-.669-.281z" />
		</SvgIcon>
	);
}

function PlusSquare(props: any) {
	return (
		<SvgIcon fontSize="inherit" style={{ width: 14, height: 14 }} {...props}>
			<path d="M22.047 22.074v0 0-20.147 0h-20.12v0 20.147 0h20.12zM22.047 24h-20.12q-.803 0-1.365-.562t-.562-1.365v-20.147q0-.776.562-1.351t1.365-.575h20.147q.776 0 1.351.575t.575 1.351v20.147q0 .803-.575 1.365t-1.378.562v0zM17.873 12.977h-4.923v4.896q0 .401-.281.682t-.682.281v0q-.375 0-.669-.281t-.294-.682v-4.896h-4.923q-.401 0-.682-.294t-.281-.669v0q0-.401.281-.682t.682-.281h4.923v-4.896q0-.401.294-.682t.669-.281v0q.401 0 .682.281t.281.682v4.896h4.923q.401 0 .682.281t.281.682v0q0 .375-.281.669t-.682.294z" />
		</SvgIcon>
	);
}

function TransitionComponent(props: any) {
	const style = useSpring({
		from: { opacity: 0, transform: "translate3d(20px,0,0)" },
		to: {
			opacity: props.in ? 1 : 0,
			transform: `translate3d(${props.in ? 0 : 20}px,0,0)`,
		},
	});

	return (
		<animated.div style={style}>
			<Collapse {...props} />
		</animated.div>
	);
}

TransitionComponent.propTypes = {
	in: PropTypes.bool,
};

interface StyledTreeItemProps extends TreeItemProps {
	isChecked?: boolean;
}

const StyledTreeItem = styled(TreeItem, {
	shouldForwardProp: (prop) => prop !== "isChecked",
})<StyledTreeItemProps>(({ theme, isChecked }) => ({
	iconContainer: {
		"& .close": {
			opacity: 0.3,
		},
	},
	group: {
		marginLeft: 7,
		paddingLeft: 18,
		borderLeft: `1px dashed ${alpha(theme.palette.text.primary, 0.4)}`,
	},
	label: {
		backgroundColor: (props: any) =>
			props.isChecked
				? alpha(theme.palette.primary.main, 0.8) + " !important"
				: "inherit",
	},
}));

const StyledTreeItemWithTransition = (props: StyledTreeItemProps) => (
	<StyledTreeItem {...props} />
);

const ModalTreeFilterControl: React.FC<ModalTreeFilterControlProps> = (
	props,
) => {
	const {
		onClose,
		value: valueProp,
		open,
		title,
		data,
		selectBranch,
		selectActive,
		selectInternal,
		multipleSelectNodes,
		onHide,
		listAssetDrawings,
		setListAssetDrawings,
		onSuccess,
		isLoading,
		mostUsedCount,
		sWithParent = [],
		dialogMaxWidth = "sm",
		allowSearch = false,
		...other
	} = props;

	const [value, setValue] = useState<number | undefined>(valueProp);
	const [expanded, setExpanded] = useState<string[]>([]);
	const treeRef = useRef<HTMLUListElement>(null);
	const [selectedNodes, setSelectedNodes] = useState<number[]>([]);
	const [searchTerm, setSearchTerm] = useState("");
	const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
	const [showSearchResults, setShowSearchResults] = useState(false);
	const [focusedSearchIndex, setFocusedSearchIndex] = useState(-1);
	const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const searchContainerRef = useRef<HTMLDivElement>(null);
	const searchResultsRefs = useRef<(HTMLElement | null)[]>([]);
	const searchInputRef = useRef<HTMLInputElement>(null);

	useEffect(() => {
		if (!open) {
			setValue(valueProp);
			// Clear search when dialog closes
			setSearchTerm("");
			setSearchResults([]);
			setShowSearchResults(false);
			setFocusedSearchIndex(-1);
		} else {
			if (valueProp !== undefined) {
				expandNode(valueProp);
				// Scroll to the selected node when dialog opens
				setTimeout(() => {
					const nodeElement = document.getElementById(valueProp.toString());
					if (nodeElement) {
						nodeElement.scrollIntoView({
							behavior: "smooth",
							block: "center",
						});
					}
				}, 300);
			}
			// Auto-focus search input when dialog opens and search is allowed
			if (allowSearch) {
				setTimeout(() => {
					// Try multiple ways to focus the search input
					if (searchInputRef.current) {
						searchInputRef.current.focus();
					} else {
						// Fallback: find input by placeholder
						const searchInput = document.querySelector(
							'input[placeholder*="Search"]',
						) as HTMLInputElement;
						if (searchInput) {
							searchInput.focus();
						}
					}
				}, 200);
			}
		}
	}, [valueProp, open, allowSearch]);

	useEffect(() => {
		if (Array.isArray(listAssetDrawings) && listAssetDrawings.length >= 1) {
			setSelectedNodes(listAssetDrawings);
			const expandedBranches = findBranchesWithSelectedNodes(
				data,
				listAssetDrawings,
			);
			setExpanded(expandedBranches);
		}
	}, [listAssetDrawings, data]);

	const findBranchesWithSelectedNodes = (
		node: TreeNode,
		selectedNodes: number[],
	): string[] => {
		let branches: string[] = [];

		if (selectedNodes.includes(node.id)) {
			branches.push(node.id.toString());
		}

		if (Array.isArray(node.children)) {
			for (const child of node.children) {
				const childBranches = findBranchesWithSelectedNodes(
					child,
					selectedNodes,
				);
				if (childBranches.length > 0) {
					branches.push(node.id.toString());
					branches = branches.concat(childBranches);
				}
			}
		}

		return branches;
	};

	const handleCancel = () => {
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		setFocusedSearchIndex(-1);
		setValue(valueProp);
		onClose();
		if (onHide) onHide(false);
	};

	const handleOk = () => {
		let findNode = getNodeData(data, value !== undefined ? value : -1);
		if (findNode === null) findNode = {} as TreeNode;
		var activeFilter =
			(selectActive && findNode.isActive) || selectActive !== true;
		var internalFilter =
			(selectInternal && findNode.isInternal) || selectInternal !== true;
		var branchFilter = selectBranch && !findNode.isLeaf;
		if (findNode.isLeaf) {
			if (activeFilter && internalFilter) {
				onClose(value, findNode.name, findNode);
				if (onSuccess) onSuccess(true);
			}
		} else {
			if (branchFilter) {
				onClose(value, findNode.name, findNode);
				if (onSuccess) onSuccess(true);
			}
		}
	};

	const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
		if (event.key === "Escape") {
			// Clear search if showing results, otherwise close dialog
			if (showSearchResults) {
				setSearchTerm("");
				setSearchResults([]);
				setShowSearchResults(false);
				setFocusedSearchIndex(-1);
			} else {
				// Reset everything when closing with ESC
				setSearchTerm("");
				setSearchResults([]);
				setShowSearchResults(false);
				setFocusedSearchIndex(-1);
				setValue(valueProp);
				onClose();
			}
			return;
		}

		// Handle keyboard navigation in search results
		if (showSearchResults && searchResults.length > 0) {
			if (event.key === "ArrowDown") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex < searchResults.length - 1
						? focusedSearchIndex + 1
						: 0;
				setFocusedSearchIndex(newIndex);
				// Scroll to the focused
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "ArrowUp") {
				event.preventDefault();
				const newIndex =
					focusedSearchIndex > 0
						? focusedSearchIndex - 1
						: searchResults.length - 1;
				setFocusedSearchIndex(newIndex);
				// Scroll to the focused
				setTimeout(() => scrollFocusedItemIntoView(newIndex), 0);
			} else if (event.key === "Enter" && focusedSearchIndex >= 0) {
				event.preventDefault();
				const selectedResult = searchResults[focusedSearchIndex];
				if (selectedResult) {
					handleSearchResultClick(selectedResult);
				}
			}
		} else if (event.key === "Enter" && !showSearchResults) {
			event.preventDefault();
			handleOk();
		}
	};

	const handleCheckboxClick = (event: React.MouseEvent<HTMLButtonElement>) => {
		event.stopPropagation();
	};

	const getNodeParent = (
		node: TreeNode,
		id: number,
		chain: string[] = [],
	): string[] => {
		if (!node || node.id === undefined) {
			return [];
		}

		if (node.id === id) {
			chain.push(node.id.toString());
			return chain;
		}

		if (Array.isArray(node.children)) {
			chain.push(node.id.toString());
			for (let i = 0; i < node.children.length; i++) {
				const result = getNodeParent(node.children[i], id, chain.slice());
				if (result.length > 0) {
					return result;
				}
			}
		}

		return [];
	};

	const expandNode = (nodeId: number) => {
		const pathToNode = getNodeParent(data, nodeId, []);
		pathToNode.push(nodeId.toString());

		// Merge with existing expanded nodes to preserve current expansions
		const mergedExpanded = Array.from(new Set([...expanded, ...pathToNode]));
		setExpanded(mergedExpanded);
	};

	const handleChange = (
		event: React.SyntheticEvent,
		nodeIds: string | null,
	) => {
		if (nodeIds === null) return;
		let findNode = getNodeData(
			data,
			nodeIds !== undefined ? Number.parseInt(nodeIds) : -1,
		);

		if (
			findNode?.hasOwnProperty("isSelectable") &&
			findNode.isSelectable === false
		) {
			return;
		}

		setValue(Number.parseInt(nodeIds));
	};

	const handleToogle = (
		event: React.SyntheticEvent,
		itemId: string,
		isExpanded: boolean,
	) => {
		if (isExpanded) {
			setExpanded((prev) => (prev.includes(itemId) ? prev : [...prev, itemId]));
		} else {
			setExpanded((prev) => prev.filter((id) => id !== itemId));
		}
	};

	const getNodeData = (node: TreeNode, id: number): TreeNode | null => {
		if (id === undefined) return null;
		if (node.id === id) return node;
		if (Array.isArray(node.children)) {
			var i;
			var result = null;
			for (i = 0; result == null && i < node.children.length; i++) {
				result = getNodeData(node.children[i], id);
			}
			return result;
		}
		return null;
	};

	const handleCheckboxChange = (node: TreeNode) => {
		if (node) {
			const isSelected = selectedNodes.includes(node.id);
			let updatedSelectedNodes: number[];

			if (isSelected) {
				updatedSelectedNodes = selectedNodes.filter((id) => id !== node.id);
			} else {
				updatedSelectedNodes = [...selectedNodes, node.id];
			}

			setSelectedNodes(updatedSelectedNodes);

			if (setListAssetDrawings) {
				setListAssetDrawings(updatedSelectedNodes);
			}

			const expandedBranches = findBranchesWithSelectedNodes(
				data,
				updatedSelectedNodes,
			);
			setExpanded(expandedBranches);
		}
	};

	function treeToArray(
		nodes: TreeNode | TreeNode[],
		currentParentName: string | undefined = undefined,
	): TreeNode[] {
		let treeArray: TreeNode[] = [];

		const nodesToProcess = Array.isArray(nodes) ? nodes : [nodes];

		nodesToProcess.forEach((node) => {
			if (currentParentName !== undefined) {
				node.parentName = currentParentName;
			} else {
				delete node.parentName;
			}

			treeArray.push(node);

			if (node.children && node.children.length > 0) {
				treeArray = treeArray.concat(treeToArray(node.children, node.name));
			}
		});

		return treeArray;
	}

	// Helper function to scroll focused into view
	const scrollFocusedItemIntoView = (index: number) => {
		if (searchResultsRefs.current[index]) {
			searchResultsRefs.current[index]?.scrollIntoView({
				behavior: "smooth",
				block: "nearest",
			});
		}
	};

	const buildNodePath = (
		node: TreeNode,
		targetId: number,
		currentPath: string[] = [],
	): string[] | null => {
		if (node.id === targetId) {
			return [...currentPath, node.name];
		}

		if (node.children) {
			for (const child of node.children) {
				const result = buildNodePath(child, targetId, [
					...currentPath,
					node.name,
				]);
				if (result) {
					return result;
				}
			}
		}

		return null;
	};

	// Pre-build search index for better performance with large trees
	const searchIndex = useMemo((): SearchResult[] => {
		const index: SearchResult[] = [];

		const buildIndex = (currentNode: TreeNode, path: string[] = []) => {
			const nodePath = [...path, currentNode.name];
			const pathStr = nodePath.join("/");

			// Determine if node has children - use isLeaf if available, otherwise check children array
			const hasChildren =
				currentNode.isLeaf === undefined
					? Boolean(currentNode.children && currentNode.children.length > 0)
					: !currentNode.isLeaf;

			index.push({
				id: currentNode.id,
				name: currentNode.name,
				path: pathStr,
				isCode: currentNode.isCode,
				hasChildren: hasChildren,
				node: currentNode,
			});

			if (currentNode.children) {
				for (const child of currentNode.children) {
					buildIndex(child, nodePath);
				}
			}
		};

		if (data) {
			buildIndex(data);
		}

		return index;
	}, [data]);

	const searchNodes = (searchTerm: string): SearchResult[] => {
		const lowerSearchTerm = searchTerm.toLowerCase();
		return searchIndex.filter((item) =>
			item.name.toLowerCase().includes(lowerSearchTerm),
		);
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const term = event.target.value;
		setSearchTerm(term);

		// Clear existing timeout
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current);
		}

		if (term.trim() === "" || term.length < 2) {
			setSearchResults([]);
			setShowSearchResults(false);
			return;
		}

		// Add debounce
		searchTimeoutRef.current = setTimeout(() => {
			const results = searchNodes(term);
			setSearchResults(results);
			setShowSearchResults(results.length > 0);
			// Auto-focus first result if results are available
			const newFocusIndex = results.length > 0 ? 0 : -1;
			setFocusedSearchIndex(newFocusIndex);
			// Clear refs array and prepare for new results
			searchResultsRefs.current = new Array(results.length).fill(null);
			// Scroll to first when results appear
			if (newFocusIndex >= 0) {
				setTimeout(() => scrollFocusedItemIntoView(newFocusIndex), 100);
			}
		}, 250);
	};

	const handleSearchResultClick = (result: SearchResult) => {
		// Get path to the selected node
		const pathToNode = getNodeParent(data, result.id, []);

		// Merge with existing expanded nodes to avoid collapsing other parts of the tree
		const mergedExpanded = Array.from(new Set([...expanded, ...pathToNode]));
		setExpanded(mergedExpanded);

		// Select the node
		setValue(result.id);

		// Clear search
		setSearchTerm("");
		setSearchResults([]);
		setShowSearchResults(false);
		setFocusedSearchIndex(-1);

		if (searchInputRef.current) {
			searchInputRef.current.blur();
		}

		setTimeout(() => {
			const nodeElement = document.getElementById(result.id.toString());
			if (nodeElement) {
				nodeElement.scrollIntoView({
					behavior: "smooth",
					block: "center",
				});
			}
		}, 300);
	};

	const renderTree = (nodes: TreeNode) => (
		<StyledTreeItemWithTransition
			isChecked={selectedNodes.includes(nodes.id)}
			key={nodes.id}
			itemId={nodes.id.toString()}
			label={
				<div
					style={{
						display: "flex",
						alignItems: "center",
						padding: "4px 0",
					}}
				>
					{nodes.isCode === true && (
						<Code sx={{ marginRight: 1 }} htmlColor={"#1ABC9C"} />
					)}
					{nodes.isCode === false && (
						<Folder sx={{ marginRight: 1 }} htmlColor={"#F1C40F"} />
					)}
					<Typography
						variant="body2"
						sx={{
							fontWeight: "inherit",
							flexGrow: 1,
							...(!nodes.isActive && {
								textDecoration: "line-through",
								textDecorationColor: "red",
							}),
							...(nodes?.hasOwnProperty("isSelectable") &&
								nodes.isSelectable === false && {
									color: "grey",
								}),
						}}
					>
						<span>
							{nodes.isCode && (
								<Square
									sx={{ mr: 1, color: nodes.delayCategoryColor || "#FFFFFF" }}
									fontSize="small"
								/>
							)}
							{nodes.name}
						</span>
					</Typography>
					{multipleSelectNodes && (
						<Checkbox
							sx={{ transform: "scale(0.8)" }}
							size="small"
							color="primary"
							checked={selectedNodes.includes(nodes.id)}
							onChange={() => {
								handleCheckboxChange(nodes);
							}}
							onClick={handleCheckboxClick}
						/>
					)}
				</div>
			}
			className={"delay-item-node"}
			id={nodes.id.toString()}
			data-isbranch={!nodes.isCode}
		>
			{Array.isArray(nodes.children)
				? nodes.children.map((node: TreeNode) => renderTree(node))
				: null}
		</StyledTreeItemWithTransition>
	);

	return (
		<Dialog
			onClose={onHide ? () => onHide(false) : () => null}
			aria-label={title}
			fullWidth
			maxWidth={dialogMaxWidth}
			disableAutoFocus
			disableEnforceFocus
			disableRestoreFocus
			aria-labelledby="confirmation-dialog-title"
			open={open}
			{...other}
			onKeyDown={handleKeyDown}
		>
			<DialogTitle id="confirmation-dialog-title">{title}</DialogTitle>
			{isLoading ? (
				<div
					style={{
						width: 600,
						minHeight: 416,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<LazyLoading />
				</div>
			) : (
				<DialogContent
					dividers
					style={{
						height: "500px",
						width: "100%",
						overflow: "auto",
						flexDirection: "column",
					}}
				>
					{allowSearch && (
						<Grid2
							ref={searchContainerRef}
							style={{
								position: "sticky",
								top: -16,
								zIndex: 1000,
								backgroundColor: "#fff",
								paddingTop: 5,
								paddingBottom: 5,
								marginLeft: -24,
								marginRight: -24,
								paddingLeft: 24,
								paddingRight: 24,
							}}
						>
							<TextField
								sx={{
									marginBottom: 1,
									"& .MuiOutlinedInput-root": {
										padding: "6px 8px",
									},
									"& .MuiInputBase-input": {
										padding: "8px 0px",
									},
								}}
								placeholder="Search (min. 2 characters)..."
								variant="outlined"
								size="small"
								fullWidth
								value={searchTerm}
								onChange={handleSearchChange}
								inputRef={searchInputRef}
								slotProps={{
									input: {
										startAdornment: (
											<SearchIcon style={{ marginRight: 8, color: "#999" }} />
										),
									} as any,
								}}
							/>
							{showSearchResults && (
								<Paper
									sx={{
										position: "absolute",
										zIndex: 1300,
										width: "100%",
										maxHeight: 300,
										overflow: "auto",
										marginTop: 1,
										boxShadow: 3,
									}}
								>
									<List>
										{searchResults.map((result, index) => (
											<ListItem
												key={result.id}
												ref={(el) => {
													searchResultsRefs.current[index] = el;
												}}
												sx={{
													cursor: "pointer",
													minHeight: "auto",
													"&:hover": {
														backgroundColor: (theme) =>
															theme.palette.action.hover,
													},
													"&.focused": {
														backgroundColor: (theme) =>
															theme.palette.action.selected,
														outline: (theme) =>
															`2px solid ${theme.palette.primary.main}`,
														outlineOffset: "-2px",
													},
												}}
												className={`${
													index === focusedSearchIndex ? "focused" : ""
												}`}
												onClick={() => handleSearchResultClick(result)}
											>
												<ListItemIcon sx={{ minWidth: 32 }}>
													{result.hasChildren ? (
														<AccountTree color="primary" fontSize="small" />
													) : (
														<Settings color="primary" fontSize="small" />
													)}
												</ListItemIcon>
												<ListItemText
													primary={result.name}
													secondary={result.path}
													secondaryTypographyProps={{
														fontSize: "0.75rem",
														color: "text.secondary",
														fontStyle: "italic",
														marginTop: 0.25,
													}}
												/>
											</ListItem>
										))}
									</List>
								</Paper>
							)}
						</Grid2>
					)}
					<Box
						sx={{
							flex: 1,
							overflow: "auto",
							display: "flex",
							flexDirection: "column",
							paddingTop: (theme) => theme.spacing(1),
							paddingLeft: (theme) => theme.spacing(2),
							paddingRight: (theme) => theme.spacing(2),
							paddingBottom: (theme) => theme.spacing(2),
						}}
					>
						<SimpleTreeView
							autoFocus={false}
							sx={{
								minHeight: 350,
								flexGrow: 1,
								height: "100%",
								overflow: "auto",
								overflowX: "hidden",
								width: "100%",
							}}
							slots={{
								collapseIcon: MinusSquare,
								expandIcon: PlusSquare,
							}}
							ref={treeRef}
							selectedItems={value?.toString()}
							expandedItems={expanded}
							onSelectedItemsChange={handleChange}
							onItemExpansionToggle={handleToogle}
						>
							{data && data.id !== undefined && (
								<Grid2>
									{mostUsedCount > 0 && (
										<Grid2 size={{ md: 12 }}>
											{treeToArray(data)
												.sort((a, b) => {
													if (!a.sortOrder) return 1;
													if (!b.sortOrder) return -1;

													const valA = a.sortOrder;
													const valB = b.sortOrder;
													return valA - valB;
												})
												.slice(0, mostUsedCount)
												.map((node) => (
													<Button
														onClick={() => {
															onClose(node.id, node.name);
															if (onSuccess) onSuccess(true);
														}}
														style={{ marginRight: 5, marginBottom: 8 }}
													>
														<Chip
															label={
																sWithParent.includes(node.name)
																	? `${node.parentName}/${node.name}`
																	: node.name
															}
															size="medium"
															variant="outlined"
															style={{ cursor: "pointer" }}
														/>
													</Button>
												))}
										</Grid2>
									)}
									{renderTree(data)}
								</Grid2>
							)}
						</SimpleTreeView>
					</Box>
				</DialogContent>
			)}
			<DialogActions>
				<Button onClick={handleCancel} color="primary">
					Cancel
				</Button>
				<Button onClick={() => handleOk()} color="primary">
					OK
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export { ModalTreeFilterControl };
